/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import AssetService from "../../services/AssetService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdTag } from "../../utils/Antd";
import util from "../../utils/util";
import Tabs, { PageHeading } from "./Tabs";
import plus from "../assets/icons/plus-solid.svg"
import edit from "../assets/icons/pen-to-square-solid.svg"
import xmark from "../assets/icons/circle-xmark-solid.svg"
import search from "../assets/icons/magnifying-glass-solid.svg"
import * as XLSX from 'xlsx';
import {
    Input,
    Button,
    message,
    Modal,
    Tabs as AntTabs,
    Upload,
    Table,
    Alert,
    Divider,
    Progress,
} from 'antd';
import {
    ExclamationCircleOutlined,
    UploadOutlined,
    DownloadOutlined,
    InboxOutlined,
} from '@ant-design/icons';

const { confirm } = Modal;
const { Dragger } = Upload;

const CATEGORY_TABS = [
    { k: 'Vehicle',     v: 'Vehicle Models',    asset_cat_id: '8', moduleKey: 'vehicle' },
    { k: 'Medical',     v: 'Medical Models',    asset_cat_id: '3', moduleKey: 'medical' },
    { k: 'Non Medical', v: 'Non Medical Models', asset_cat_id: '2', moduleKey: 'non_medical' },
    { k: 'Supplies',    v: 'Supplies Models',   asset_cat_id: '9', moduleKey: 'supplies' },
];

export default function Model() {
    const modules      = util.getModules();
    const filteredTabs = CATEGORY_TABS.filter(t => modules[t.moduleKey] === 1);
    const [tab, setTab] = useState(filteredTabs.length > 0 ? filteredTabs[0].k : '');

    useEffect(() => { return () => { message.destroy(); }; }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><PageHeading /></div>
                <div className="my-auto ml-auto"></div>
            </div>
            <div className="page-pad">
                <div className="tabbable-custom">
                    <Tabs active="models" />
                    <div className="tab-content">
                        <div className="tab-pane fade1 show active">
                            <AntTabs
                                activeKey={tab}
                                destroyInactiveTabPane
                                onChange={setTab}
                                items={filteredTabs.map(rw => ({
                                    key: rw.k,
                                    label: rw.v,
                                    children: (
                                        <ModelList
                                            asset_cat_id={rw.asset_cat_id}
                                            tabName={rw.v}
                                        />
                                    )
                                }))}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── ModelList ─────────────────────────────────────────────────────────────────

function ModelList({ asset_cat_id, tabName }) {
    const [result, setResult] = useState({ data: [], page: {} });
    const sdataRef  = useRef({ p: 1, ps: 25, asset_cat_id });
    const formRef   = useRef();
    const importRef = useRef();

    const list = (p, ps) => {
        sdataRef.current.p            = p  || 1;
        sdataRef.current.ps           = ps || sdataRef.current.ps;
        sdataRef.current.asset_cat_id = asset_cat_id;
        util.showLoader();
        AssetService.models(sdataRef.current).then(({ data }) => {
            setResult(data.result);
        }).catch(e => { message.error(e.message); }).finally(() => { util.hideLoader(); });
    };

    const deleteRecord = (id) => {
        message.destroy();
        confirm({
            title: 'Do you want to delete this model?',
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes', okType: 'danger', cancelText: 'No',
            onOk() {
                util.showLoader();
                AssetService.deleteModel(id)
                    .then(({ data }) => { message.success(data.message || 'Deleted'); list(); })
                    .catch(e => { message.error(e.message); })
                    .finally(() => { util.hideLoader(); });
            },
        });
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([['Model Name'], ['Example Model']]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Models');
        ws['!cols'] = [{ width: 35 }];
        XLSX.writeFile(wb, `${tabName.toLowerCase().replace(/\s+/g, '_')}_template.xlsx`);
        message.success('Template downloaded');
    };

    useEffect(() => {
        list();
        return () => { message.destroy(); };
    }, [asset_cat_id]);

    return (
        <div>
            {result.data.length > 0 && (
                <div className="text-secondary mb8">
                    Showing {result.page.start + 1} – {result.page.start + result.page.total} of {result.page.total_records} records.
                </div>
            )}

            <div className="d-flex tbl-search-head">
                <div className="my-auto">
                    <SearchForm dataRef={sdataRef} onSearch={list} />
                </div>
                <div className="ml-auto my-auto d-flex" style={{ gap: 8 }}>
                    <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Template</Button>
                    <Button icon={<UploadOutlined />}   onClick={() => importRef.current.openForm()}>Import</Button>
                    <Button type="primary"              onClick={() => formRef.current.openForm()}>
                        <img src={plus} alt='' className='mr5' height={15}/> Add
                    </Button>
                </div>
            </div>

            <If cond={result.data.length}>
                <div className="table-responsive">
                    <table className="table table-bordered table-md table-striped table-hover m-0">
                        <thead className="thead-light text-uppercase table-text-vmid">
                            <tr>
                                <th className="w20">SN</th>
                                <th>Model Name</th>
                                <th className="w60">Status</th>
                                <th className="w70"></th>
                            </tr>
                        </thead>
                        <tbody className="table-text-top">
                            {result.data.map((v, i) => (
                                <tr key={i}>
                                    <td>{result.page.start + i + 1}.</td>
                                    <td>{v.name}</td>
                                    <td className="nowrap">
                                        {v.status
                                            ? <AntdTag type="success">Active</AntdTag>
                                            : <AntdTag type="danger">Inactive</AntdTag>}
                                    </td>
                                    <td className="text-center">
                                        <Button.Group size="small">
                                            <Button type="default" onClick={() => formRef.current.openForm(v)}>
                                                <img src={edit} alt='' height={15}/>
                                            </Button>
                                            <Button type="default" onClick={() => deleteRecord(v.id)}>
                                                <img src={xmark} alt='' height={15}/>
                                            </Button>
                                        </Button.Group>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex tbl-foot-bx">
                    <AntdPaging
                        onChange={list}
                        total={result.page.total_records}
                        current={result.page.cur_page}
                        pageSize={sdataRef.current.ps}
                        showSizeChanger
                    />
                </div>
            </If>
            <If cond={!result.data.length}>
                <div className="no-rec">No record found</div>
            </If>

            <AddForm
                ref={formRef}
                callback={list}
                pageno={sdataRef.current.p}
                asset_cat_id={asset_cat_id}
            />
            <ImportForm
                ref={importRef}
                callback={list}
                asset_cat_id={asset_cat_id}
                tabName={tabName}
            />
        </div>
    );
}

// ── ImportForm ────────────────────────────────────────────────────────────────

const ImportForm = forwardRef(({ callback, asset_cat_id, tabName }, ref) => {
    const [showForm,         setShowForm]         = useState(false);
    const [fileList,         setFileList]         = useState([]);
    const [previewData,      setPreviewData]       = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [importing,        setImporting]         = useState(false);
    const [importProgress,   setImportProgress]   = useState(0);
    const [importSummary,    setImportSummary]     = useState(null);

    const resetForm = () => {
        setFileList([]); setPreviewData([]); setValidationErrors([]);
        setImportProgress(0); setImportSummary(null);
    };

    const handleFileUpload = (info) => {
        const { fileList: newList } = info;
        setFileList(newList.slice(-1));
        const file = newList[0]?.originFileObj;
        if (!file) return;
        if (!file.name.toLowerCase().match(/\.xlsx?$/)) {
            message.error('Excel files only (.xlsx or .xls)');
            setFileList([]);
            return;
        }
        parseExcelFile(file);
    };

    const parseExcelFile = (file) => {
        util.showLoader();
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb       = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const ws       = wb.Sheets[wb.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });

                const firstRow = jsonData[0];
                const isHeader = firstRow?.length > 0 &&
                    typeof firstRow[0] === 'string' &&
                    (firstRow[0].toLowerCase().includes('model') || firstRow[0].toLowerCase().includes('name'));
                const dataRows = isHeader ? jsonData.slice(1) : jsonData;

                if (!dataRows.length) { message.error('No data rows found'); return; }

                const { valid, errors } = processRows(dataRows);
                setPreviewData(valid);
                setValidationErrors(errors);
                if (valid.length) message.success(`Found ${valid.length} valid model(s)`);
                else              message.warning('No valid models found');
            } catch (err) {
                message.error('Failed to parse file: ' + err.message);
            } finally {
                util.hideLoader();
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const processRows = (rows) => {
        const valid = [], errors = [], seen = new Set();
        rows.forEach((row, i) => {
            const name = (row[0] || '').toString().trim();
            const n    = i + 1;
            if (!name)            { errors.push(`Row ${n}: Name is required`); return; }
            if (name.length > 100){ errors.push(`Row ${n}: Name too long (max 100 chars)`); return; }
            const key = name.toLowerCase();
            if (seen.has(key))    { errors.push(`Row ${n}: Duplicate in file — "${name}" skipped`); return; }
            seen.add(key);
            valid.push({ name, asset_cat_id, status: '1' });
        });
        return { valid, errors };
    };

    const performImport = async () => {
        if (!previewData.length) { message.error('No valid data to import'); return; }
        setImporting(true); setImportProgress(0); setImportSummary(null);

        let successCount = 0, skippedCount = 0;
        const errorMessages = [];

        for (let i = 0; i < previewData.length; i++) {
            try {
                await AssetService.saveModel(previewData[i]);
                successCount++;
            } catch (e) {
                const msg = e.response?.data?.message || e.message || '';
                if (/duplicate|already exist/i.test(msg)) {
                    skippedCount++;
                    errorMessages.push(`"${previewData[i].name}" already exists — skipped`);
                } else {
                    errorMessages.push(`Row ${i + 1}: ${msg}`);
                }
            }
            setImportProgress(Math.round(((i + 1) / previewData.length) * 100));
        }

        setImportSummary({ successCount, skippedCount, errorMessages });
        setImporting(false);
        if (successCount > 0) { message.success(`${successCount} model(s) imported`); callback(1); }
        if (skippedCount > 0 && successCount === 0) message.warning('All rows skipped — likely duplicates');
    };

    useImperativeHandle(ref, () => ({
        openForm() { resetForm(); setShowForm(true); }
    }));

    const previewCols = [
        { title: '#',    key: 'i',    width: 50, render: (_, __, i) => i + 1 },
        { title: 'Model Name', dataIndex: 'name', key: 'name' },
    ];

    const close = () => { setShowForm(false); resetForm(); };

    return (
        <Modal
            title={`Import ${tabName} from Excel`}
            open={showForm}
            onCancel={close}
            width={700}
            footer={[
                <Button key="cancel" onClick={close} disabled={importing}>Cancel</Button>,
                <Button
                    key="import"
                    type="primary"
                    onClick={performImport}
                    disabled={!previewData.length || importing || !!importSummary}
                    loading={importing}
                >
                    {importing ? `Importing… ${importProgress}%` : `Import ${previewData.length} Models`}
                </Button>
            ]}
        >
            {!importSummary && (
                <>
                    <Dragger
                        fileList={fileList}
                        onChange={handleFileUpload}
                        beforeUpload={() => false}
                        accept=".xlsx,.xls"
                        maxCount={1}
                        disabled={importing}
                        style={{ marginBottom: 16 }}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Click or drag Excel file to upload</p>
                        <p className="ant-upload-hint">Column A = Model Name. Duplicates are skipped automatically.</p>
                    </Dragger>

                    {importing && <Progress percent={importProgress} status="active" style={{ marginBottom: 12 }} />}

                    {validationErrors.length > 0 && (
                        <Alert
                            message={`${validationErrors.length} row(s) skipped`}
                            description={
                                <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 100, overflowY: 'auto' }}>
                                    {validationErrors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
                                    {validationErrors.length > 8 && <li>…and {validationErrors.length - 8} more</li>}
                                </ul>
                            }
                            type="warning" showIcon style={{ marginBottom: 12 }}
                        />
                    )}

                    {previewData.length > 0 && (
                        <>
                            <Divider>Preview ({previewData.length} rows)</Divider>
                            <Table
                                columns={previewCols}
                                dataSource={previewData}
                                rowKey={(_, i) => i}
                                size="small"
                                pagination={{ pageSize: 8, showTotal: t => `${t} models` }}
                                scroll={{ y: 220 }}
                            />
                        </>
                    )}

                    <Alert
                        message="Format"
                        description={`Column A = Model Name. All imported models will be tagged to "${tabName}". Duplicates within the file and in the database are skipped.`}
                        type="info" showIcon style={{ marginTop: 12 }}
                    />
                </>
            )}

            {importSummary && (
                <Alert
                    message="Import Complete"
                    description={
                        <div>
                            <p><strong>Imported:</strong> {importSummary.successCount}</p>
                            {importSummary.skippedCount > 0 && (
                                <p><strong>Skipped (duplicates):</strong> {importSummary.skippedCount}</p>
                            )}
                            {importSummary.errorMessages.length > 0 && (
                                <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#cf1322', fontSize: 12, maxHeight: 120, overflowY: 'auto' }}>
                                    {importSummary.errorMessages.map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                            )}
                        </div>
                    }
                    type={importSummary.successCount > 0 ? 'success' : 'warning'}
                    showIcon
                />
            )}
        </Modal>
    );
});

// ── SearchForm ────────────────────────────────────────────────────────────────

const SearchForm = ({ dataRef, onSearch }) => {
    const [data, setData] = useState({ ...dataRef.current });
    const handleChange = (v, k) => { data[k] = v; setData({ ...data }); };
    useEffect(() => { dataRef.current = { ...data }; }, [data]);
    useEffect(() => {
        setData(d => ({ ...d, p: dataRef.current.p, ps: dataRef.current.ps }));
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                <div>
                    <Input placeholder="Search" allowClear value={data.k}
                        onChange={e => handleChange(e.target.value, 'k')} />
                </div>
                <div>
                    <Button type="primary"
                        icon={<img src={search} alt='' className='mr5' height={15}/>}
                        onClick={() => onSearch()} />
                </div>
            </div>
        </form>
    );
};

// ── AddForm ───────────────────────────────────────────────────────────────────

const AddForm = forwardRef(({ callback, pageno, asset_cat_id }, ref) => {
    const [showForm, setShowForm] = useState(false);
    const [data, setData]         = useState({});

    const handleChange = (v, k) => setData(prev => ({ ...prev, [k]: v }));

    const save = () => {
        message.destroy();
        util.showLoader();
        AssetService.saveModel(data)
            .then(({ data: res }) => {
                message.success(res.message || 'Saved');
                callback(data.id ? pageno : 1);
                setShowForm(false);
            })
            .catch(e => { message.error(e.message); })
            .finally(() => { util.hideLoader(); });
    };

    useImperativeHandle(ref, () => ({
        openForm(dtl) {
            setData(dtl ? { ...dtl } : { status: '1', asset_cat_id });
            setShowForm(true);
        }
    }));

    return (
        <Modal
            title={`${data.id ? 'Edit' : 'Add'} Model`}
            open={showForm}
            okText="Save"
            onOk={save}
            onCancel={() => setShowForm(false)}
            destroyOnClose
            maskClosable={false}
            width={400}
        >
            <form onSubmit={e => { e.preventDefault(); save(); }} autoComplete="off" spellCheck="false">
                <div className="row mingap">
                    <div className="col-md-12 form-group">
                        <label className="req">Model Name</label>
                        <Input value={data.name || ''} onChange={e => handleChange(e.target.value, 'name')} />
                    </div>
                    <div className="col-md-12 form-group">
                        <label className="req">Status</label>
                        <div>
                            <AntdSelect
                                options={[{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }]}
                                value={data.status}
                                onChange={v => handleChange(v, 'status')}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
});